import { ManualABNTData } from './fleetone-abnt-content';

export const utmGuideManual: ManualABNTData = {
  metadata: {
    title: "Guia de Rastreamento de Campanhas UTM",
    subtitle: "Como padronizar UTMs, construir links e analisar resultados",
    version: "v1.0",
    date: new Date().toISOString(),
    author: "PortalONE Suite",
    keywords: ["marketing", "utm", "campanhas", "rastreamento", "analytics", "leads"],
    license: "© PortalONE Suite",
    year: "2025",
    format: "ABNT"
  },
  
  preTextual: {
    cover: {
      institution: "PortalONE Suite",
      title: "Guia de Rastreamento de Campanhas UTM",
      subtitle: "Marketing Digital",
      version: "v1.0",
      date: "2025",
      badge: "📊 Marketing"
    },
    abstract: "Este guia apresenta o sistema de rastreamento de campanhas UTM do PortalONE, permitindo identificar e analisar a origem de leads através de parâmetros padronizados. Inclui práticas recomendadas, exemplos por plataforma e instruções para análise de performance."
  },
  
  body: {
    sections: [
      {
        number: "1",
        title: "COMO CONFIGURAR LINKS DE CAMPANHA",
        level: 1,
        content: `Links UTM permitem rastrear a origem de visitantes e leads adicionando parâmetros à URL de destino. Esses parâmetros são capturados pelo sistema e associados aos leads gerados.`,
        subsections: [
          {
            number: "1.1",
            title: "Estrutura Base",
            level: 2,
            content: `\`\`\`
https://www.portalone.adm.br/trackingone?utm_source=google&utm_medium=cpc&utm_campaign=lancamento_gps_q1_2025
\`\`\`

<div class="callout"><strong>💡 Dica:</strong> Sempre inicie pela URL limpa (sem UTMs), depois adicione <code>?utm_...</code>. Se já existir querystring, use <code>&</code> para concatenar.</div>`
          }
        ]
      },
      {
        number: "2",
        title: "PARÂMETROS UTM",
        level: 1,
        content: `Use os parâmetros abaixo de forma consistente (preferencialmente em *lowercase* e com \`_\` para separar palavras).

| Parâmetro | Exemplo | Quando usar |
|-----------|---------|-------------|
| \`utm_source\` | \`google\`, \`facebook\`, \`newsletter\` | Fonte do tráfego |
| \`utm_medium\` | \`cpc\`, \`social\`, \`email\` | Meio/canal |
| \`utm_campaign\` | \`lancamento_gps_q1_2025\` | Nome da campanha |
| \`utm_term\` | \`rastreamento+gps\` | Palavra-chave (principalmente em search) |
| \`utm_content\` | \`video_15s\`, \`banner_a\` | Variação do criativo/anúncio |
| \`utm_id\` (opcional) | \`camp_123\` | Identificador interno da campanha |`
      },
      {
        number: "3",
        title: "EXEMPLOS POR PLATAFORMA",
        level: 1,
        content: `Veja como estruturar URLs UTM para cada plataforma de marketing.`,
        subsections: [
          {
            number: "3.1",
            title: "Google Ads",
            level: 2,
            content: `\`\`\`
https://www.portalone.adm.br/trackingone?utm_source=google&utm_medium=cpc&utm_campaign=search_gps_brasil&utm_term=rastreamento+gps
\`\`\`

**Parâmetros recomendados:**
- \`utm_source\`: \`google\`
- \`utm_medium\`: \`cpc\`
- \`utm_term\`: Use \`{keyword}\` para inserção dinâmica de termos`
          },
          {
            number: "3.2",
            title: "Facebook/Instagram Ads",
            level: 2,
            content: `\`\`\`
https://www.portalone.adm.br/trackingone?utm_source=facebook&utm_medium=social&utm_campaign=awareness_fleet_q1&utm_content=video_15s
\`\`\`

**Parâmetros recomendados:**
- \`utm_source\`: \`facebook\` ou \`instagram\`
- \`utm_medium\`: \`social\`
- \`utm_content\`: Identificador do criativo (ex: \`video_15s\`, \`carousel_a\`)`
          },
          {
            number: "3.3",
            title: "LinkedIn Ads",
            level: 2,
            content: `\`\`\`
https://www.portalone.adm.br/fleet?utm_source=linkedin&utm_medium=cpc&utm_campaign=b2b_fleet_executivos
\`\`\`

**Parâmetros recomendados:**
- \`utm_source\`: \`linkedin\`
- \`utm_medium\`: \`cpc\` ou \`social\`
- \`utm_campaign\`: Nome descritivo da campanha B2B`
          },
          {
            number: "3.4",
            title: "E-mail Marketing",
            level: 2,
            content: `\`\`\`
https://www.portalone.adm.br/trackingone?utm_source=newsletter&utm_medium=email&utm_campaign=lancamento_chips_m2m
\`\`\`

**Parâmetros recomendados:**
- \`utm_source\`: \`newsletter\` ou nome da lista
- \`utm_medium\`: \`email\`
- \`utm_content\`: Posição do link (ex: \`header\`, \`cta_principal\`)`
          }
        ]
      },
      {
        number: "4",
        title: "LANDING PAGES DISPONÍVEIS",
        level: 1,
        content: `As seguintes landing pages do PortalONE estão preparadas para receber parâmetros UTM:

**Páginas de Produto:**
- **TrackingONE:** \`/trackingone\` — Rastreamento GPS
- **FleetONE:** \`/fleet\` — Gestão de Frotas
- **NoteONE:** \`/noteone\` — Emissão de Notas Fiscais

<div class="callout callout--ok"><strong>✅ Todas as páginas capturam automaticamente:</strong> Parâmetros UTM, origem do formulário, sistema de interesse, data e hora do acesso.</div>`
      },
      {
        number: "5",
        title: "ONDE VISUALIZAR RESULTADOS",
        level: 1,
        content: `Os dados de rastreamento ficam disponíveis na área administrativa do PortalONE.`,
        subsections: [
          {
            number: "5.1",
            title: "Passo a Passo",
            level: 2,
            content: `1. Acesse o **Admin → Leads & CRM**
2. Filtre por sistema (TrackingONE, FleetONE, etc.)
3. Clique em um lead para ver dados UTM completos
4. Use os filtros de data para análise por período

**Dados exibidos por lead:**
- \`utm_source\` — Origem do tráfego
- \`utm_medium\` — Canal utilizado
- \`utm_campaign\` — Nome da campanha
- \`utm_term\` — Termo de busca (quando aplicável)
- \`utm_content\` — Variação do criativo
- \`form_source\` — Tipo de formulário (modal_trial, contact_form, etc.)
- \`system\` — Sistema de interesse (gps_tracking, fleet, noteone)`
          }
        ]
      },
      {
        number: "6",
        title: "BOAS PRÁTICAS",
        level: 1,
        content: `Siga estas recomendações para manter consistência e facilitar análises.`,
        subsections: [
          {
            number: "6.1",
            title: "✅ Faça",
            level: 2,
            content: `- Use **nomes descritivos e consistentes**
- Padronize em **lowercase** (tudo minúsculo)
- Use **underscore** (\`_\`) ao invés de espaços
- Documente suas campanhas em planilha
- Mantenha um **glossário de nomenclaturas** para a equipe
- Teste os links antes de publicar campanhas`
          },
          {
            number: "6.2",
            title: "❌ Evite",
            level: 2,
            content: `- URLs excessivamente longas (dificulta compartilhamento)
- Caracteres especiais (\`@\`, \`#\`, \`%\`, etc.)
- Espaços (use \`+\` ou \`_\`)
- Nomes genéricos como \`campanha1\`, \`teste\`
- Misturar maiúsculas e minúsculas (\`utm_Source\` vs \`utm_source\`)
- Criar variações desnecessárias que dificultam análises`
          }
        ]
      },
      {
        number: "7",
        title: "FONTES DE TRÁFEGO RASTREADAS",
        level: 1,
        content: `O sistema PortalONE rastreia automaticamente diferentes pontos de conversão.`,
        subsections: [
          {
            number: "7.1",
            title: "Por Origem (\`utm_source\`)",
            level: 2,
            content: `**Canais de conversão:**
- \`whatsapp\` — Contato via botão flutuante do WhatsApp
- \`modal_trial\` — Formulário "Testar Grátis"
- \`modal_demo\` — Formulário "Agendar Demo"
- \`modal_subscription\` — Formulário "Assinar Agora"
- \`contact_form\` — Formulário de contato geral

Esses valores são atribuídos automaticamente pelo sistema quando o usuário interage com os formulários.`
          },
          {
            number: "7.2",
            title: "Por Sistema",
            level: 2,
            content: `**Produtos rastreados:**
- \`gps_tracking\` — TrackingONE (Rastreamento GPS)
- \`fleet\` — FleetONE (Gestão de Frotas)
- \`noteone\` — NoteONE (Emissão de Notas Fiscais)

O sistema é identificado automaticamente com base na landing page acessada.`
          }
        ]
      },
      {
        number: "8",
        title: "FERRAMENTAS DE GERAÇÃO",
        level: 1,
        content: `Utilize ferramentas para criar URLs com UTMs de forma padronizada.`,
        subsections: [
          {
            number: "8.1",
            title: "Google Campaign URL Builder",
            level: 2,
            content: `**Link:** [https://ga-dev-tools.google/campaign-url-builder/](https://ga-dev-tools.google/campaign-url-builder/)

**Como usar:**
1. Cole a URL base (ex: \`https://www.portalone.adm.br/trackingone\`)
2. Preencha os campos UTM
3. Copie a URL gerada
4. Salve na planilha de controle

<div class="callout"><strong>💡 Dica:</strong> Prefira gerar URLs em uma planilha própria para manter histórico e padronização da equipe.</div>`
          },
          {
            number: "8.2",
            title: "Encurtadores de URL",
            level: 2,
            content: `Para URLs muito longas, considere usar encurtadores como:
- **Bitly** — [bitly.com](https://bitly.com)
- **TinyURL** — [tinyurl.com](https://tinyurl.com)
- **Rebrandly** — [rebrandly.com](https://rebrandly.com) (permite domínios personalizados)

<div class="callout callout--warn"><strong>⚠️ Atenção:</strong> Links encurtados podem perder parâmetros UTM se não configurados corretamente. Sempre teste antes de usar.</div>`
          }
        ]
      },
      {
        number: "9",
        title: "PLANILHA DE CONTROLE DE CAMPANHAS",
        level: 1,
        content: `Mantenha uma planilha centralizada com todas as campanhas ativas.`,
        subsections: [
          {
            number: "9.1",
            title: "Estrutura Recomendada",
            level: 2,
            content: `**Colunas sugeridas:**

| Campo | Descrição | Exemplo |
|-------|-----------|---------|
| **Nome da campanha** | Identificação interna | Lançamento GPS Q1 2025 |
| **URL completa** | Link com UTMs | \`https://...\` |
| **utm_source** | Fonte | \`google\` |
| **utm_medium** | Meio | \`cpc\` |
| **utm_campaign** | Campanha | \`lancamento_gps_q1_2025\` |
| **Data início** | Quando começa | 01/01/2025 |
| **Data fim** | Quando termina | 31/03/2025 |
| **Orçamento** | Valor investido | R$ 5.000,00 |
| **Plataforma** | Onde está rodando | Google Ads |
| **Status** | Ativo / Pausado / Finalizado | Ativo |
| **Responsável** | Quem gerencia | João Silva |`
          }
        ]
      },
      {
        number: "10",
        title: "ANÁLISE DE PERFORMANCE",
        level: 1,
        content: `Utilize os dados coletados para otimizar suas campanhas.`,
        subsections: [
          {
            number: "10.1",
            title: "Métricas Principais",
            level: 2,
            content: `**Indicadores-chave para rastreamento:**

- **Leads Gerados:** Quantidade total de contatos capturados
- **Custo por Lead (CPL):** *Investimento ÷ Leads*
- **Taxa de Conversão:** *Leads → Clientes / Leads Total × 100*
- **ROI (Retorno sobre Investimento):** *(Receita − Custo) ÷ Custo × 100*
- **Origem mais eficiente:** Qual \`utm_source\` gera mais conversões
- **Campanha com melhor custo-benefício:** Menor CPL com maior taxa de conversão`
          },
          {
            number: "10.2",
            title: "Dicas de Análise",
            level: 2,
            content: `**Como extrair insights:**

1. **Segmente por \`utm_source\`** para comparar Google Ads vs Facebook Ads
2. **Compare \`utm_campaign\`** para identificar quais anúncios performam melhor
3. **Analise \`utm_content\`** para testar criativos (vídeo vs imagem, banner A vs B)
4. **Use janelas de comparação:** Compare 7, 14 ou 30 dias para decisões rápidas
5. **Identifique padrões sazonais:** Veja se há horários/dias com melhor desempenho

<div class="callout callout--ok"><strong>✅ Ação recomendada:</strong> Revise os dados semanalmente e ajuste orçamentos/criativos das campanhas de baixo desempenho.</div>`
          }
        ]
      },
      {
        number: "11",
        title: "RELATÓRIOS DISPONÍVEIS",
        level: 1,
        content: `O sistema PortalONE oferece visualizações dos dados UTM coletados.`,
        subsections: [
          {
            number: "11.1",
            title: "Tipos de Relatórios",
            level: 2,
            content: `**Relatórios gerados automaticamente:**

- **Por Origem (\`utm_source\`):** Qual plataforma gera mais leads?
- **Por Sistema (\`system\`):** Qual produto tem mais interesse? (TrackingONE, FleetONE, NoteONE)
- **Por Campanha (\`utm_campaign\`):** Qual anúncio performa melhor?
- **Por Formulário (\`form_source\`):** Qual tipo de conversão é mais comum? (Teste Grátis, Demo, Assinatura)
- **Timeline:** Como o volume de leads varia ao longo do tempo?

<div class="callout"><strong>💡 Em breve:</strong> Dashboards interativos com filtros avançados e exportação de dados em Excel/CSV.</div>`
          }
        ]
      },
      {
        number: "12",
        title: "SUPORTE",
        level: 1,
        content: `Para dúvidas sobre rastreamento de campanhas UTM:

1. Acesse: **Admin → Leads & CRM**
2. Consulte este guia na seção **Manuais**
3. Contate o **time de desenvolvimento** via chamado de suporte

**Informações úteis para abertura de chamado:**
- URL da campanha completa (com UTMs)
- Prints dos dados UTM esperados vs capturados
- Data e hora do teste realizado
- Sistema acessado (TrackingONE, FleetONE, etc.)

<div class="callout callout--ok"><strong>✅ Lembre-se:</strong> Testes em ambiente de desenvolvimento podem não ser salvos no banco de produção. Sempre valide em produção após publicar campanhas.</div>`
      }
    ]
  },
  
  postTextual: {
    references: [
      {
        author: "Google",
        title: "Campaign URL Builder",
        year: "2025",
        url: "https://ga-dev-tools.google/campaign-url-builder/",
        accessDate: "21/10/2025"
      },
      {
        author: "PortalONE Suite",
        title: "Documentação de Leads & CRM",
        year: "2025"
      }
    ]
  }
};
